import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { KachelLink } from "@/components/KachelLink";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Building, Building2, Home, Users, ArrowLeftRight, Plus, Sparkles,
    TrendingUp, AlertCircle, CircleDot, GripVertical, ShieldCheck, CheckCircle2,
    CheckSquare, AlertTriangle, ShieldAlert, ListChecks, ChevronRight,
} from "lucide-react";
import {
    getCockpitStats, listTurnovers, listProperties, listUnits, listEntities, listTenants, seedDemo,
    erklaert, listAufgaben, listWidersprueche, arbeitsliste,
} from "@/lib/api";
import { PHASES, UNIT_TYPES, germanDate, euro, statusLabel } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import BotStatusBadge from "@/components/BotStatusBadge";
import { useEntityOrder } from "@/lib/useEntityOrder";
import { toast } from "sonner";
import { Hilfe } from "@/hilfe/Hilfe";

// Kennzahlen sind Einstiege, keine Schilder: wer "Gewerbe 7" liest, will die
// sieben sehen. Darum fuehren Kachel und jede Zeile der Aufschluesselung auf
// die passend gefilterte Liste. Ohne href bleibt alles wie zuvor --
// "Datenqualitaet" etwa hat kein sinnvolles Ziel.
const Metric = ({ label, value, icon: Icon, testid, breakdown = null, footnote = null, href = null }) => (
    <div className={`bg-white border rounded-md p-5 shadow-flat transition-colors
                     ${href ? "border-gray-200 hover:border-slate-400" : "border-gray-200"}`}
         data-testid={testid}>
        <div className="flex items-center justify-between">
            {href ? (
                <Link to={href} className="text-[11px] uppercase tracking-[0.18em] text-gray-500 hover:text-slate-900 hover:underline underline-offset-2">
                    {label}
                </Link>
            ) : (
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">{label}</div>
            )}
            <Icon size={16} className="text-gray-400" />
        </div>
        {/* Die Schriftgröße richtet sich nach der Länge: "4" und "26.686,00 €"
            teilen sich dieselbe Kachel. Mit fester Größe quoll der Betrag am
            10.09.2026 um 74 px aus der Kachel heraus — die Zahl stand halb
            außerhalb ihres weißen Kastens. */}
        <div className={`mt-3 font-heading font-bold text-slate-900 tabular-nums break-words
                         ${String(value).length > 9 ? "text-xl" : String(value).length > 5 ? "text-2xl" : "text-3xl"}`}>
            {value}
        </div>
        {breakdown && breakdown.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100 space-y-1" data-testid={`${testid}-breakdown`}>
                {breakdown.map((b) => (
                    b.href ? (
                        <Link key={b.label} to={b.href}
                              className="flex items-baseline justify-between gap-2 text-[11px] group">
                            <span className="text-gray-500 truncate group-hover:text-slate-900 group-hover:underline underline-offset-2">{b.label}</span>
                            <span className="text-slate-700 font-medium tabular-nums shrink-0">{b.value}</span>
                        </Link>
                    ) : (
                        <div key={b.label} className="flex items-baseline justify-between gap-2 text-[11px]">
                            <span className="text-gray-500 truncate">{b.label}</span>
                            <span className="text-slate-700 font-medium tabular-nums shrink-0">{b.value}</span>
                        </div>
                    )
                ))}
            </div>
        )}
        {footnote && <div className="mt-2 text-[10px] text-gray-400 leading-snug">{footnote}</div>}
    </div>
);

const typLabel = (v) => UNIT_TYPES.find((t) => t.value === v)?.label || v;

const ViewBesitz = ({ cockpit }) => {
    const entities = cockpit?.per_entity || [];
    const { ordered, handlers, reset, isReordered, dragId, overId } = useEntityOrder(entities, "entity_id");

    return (
        <div data-testid="view-besitz">
            {ordered.length > 1 && (
                <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                    <span>Reihenfolge per Drag &amp; Drop anpassen</span>
                    {isReordered && (
                        <button type="button" onClick={reset} className="text-slate-600 hover:text-slate-900 underline-offset-2 hover:underline" data-testid="reset-entity-order">
                            Reihenfolge zurücksetzen
                        </button>
                    )}
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ordered.map((e) => {
                    // Leerstand auf den beurteilbaren Teil, nicht auf alle Einheiten:
                    // die Familienstiftung hat 20 Einheiten, alle ohne Mieterdaten —
                    // "0% leer" waere dort eine Aussage ohne Grundlage.
                    const beurteilbar = (e.units || 0) - (e.unbekannt || 0);
                    const leerRate = beurteilbar > 0 ? Math.round((e.leer / beurteilbar) * 100) : null;
                    const isDragging = dragId === e.entity_id;
                    const isOver = overId === e.entity_id && dragId && dragId !== e.entity_id;
                    return (
                        <div
                            key={e.entity_id}
                            {...handlers(e)}
                            className={`bg-white border rounded-md p-5 transition-all cursor-grab active:cursor-grabbing
                                ${isDragging ? "opacity-40 border-slate-400" : "border-gray-200 hover:border-slate-400"}
                                ${isOver ? "ring-2 ring-slate-400 border-slate-400" : ""}`}
                            data-testid={`entity-card-${e.entity_id}`}
                        >
                            <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2 min-w-0">
                                    <GripVertical size={14} className="text-gray-300 mt-1 shrink-0" />
                                    <div className="min-w-0">
                                        <div className="text-[10px] uppercase tracking-[0.18em] text-gray-400">Entität</div>
                                        <Link to={`/entitaeten/${e.entity_id}`} draggable={false}
                                              onMouseDown={(ev) => ev.stopPropagation()}
                                              onDragStart={(ev) => ev.preventDefault()}
                                              onClick={(ev) => ev.stopPropagation()}
                                              className="block font-heading font-semibold text-slate-900 truncate hover:underline underline-offset-2">
                                            {e.name}
                                        </Link>
                                    </div>
                                </div>
                                <Building size={14} className="text-gray-400 shrink-0" />
                            </div>
                            <div className="mt-4 grid grid-cols-3 gap-2 text-center border-t border-gray-100 pt-3">
                                <KachelLink href={`/immobilien?entity_id=${e.entity_id}`}
                                            title={`Die ${e.properties} Immobilien dieser Entität`}>
                                    <div className="kachel-label uppercase text-gray-400">Immob.</div>
                                    <div className="text-lg font-bold text-slate-900 tabular-nums">{e.properties}</div>
                                </KachelLink>
                                <KachelLink href={`/einheiten?entity_id=${e.entity_id}`}
                                            title={`Die ${e.units} Einheiten dieser Entität`}>
                                    <div className="kachel-label uppercase text-gray-400">Einheiten</div>
                                    <div className="text-lg font-bold text-slate-900 tabular-nums">{e.units}</div>
                                </KachelLink>
                                <KachelLink
                                    href={`/einheiten?entity_id=${e.entity_id}&status=${e.unbekannt ? "unbekannt" : "leer"}`}
                                    title={e.unbekannt
                                        ? `${e.unbekannt} von ${e.units} Einheiten ohne Mieterdaten`
                                        : "Die leeren Einheiten dieser Entität"}>
                                    <div className="kachel-label uppercase text-gray-400">
                                        {e.unbekannt ? "Unbekannt" : "Leer"}
                                    </div>
                                    {e.unbekannt ? (
                                        <div className="text-lg font-bold text-orange-600 tabular-nums">
                                            {e.unbekannt}
                                        </div>
                                    ) : (
                                        <div className="text-lg font-bold text-slate-900 tabular-nums">
                                            {leerRate === null ? "—" : `${leerRate}%`}
                                        </div>
                                    )}
                                </KachelLink>
                            </div>
                            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between" draggable={false} onMouseDown={(ev) => ev.stopPropagation()} onDragStart={(ev) => ev.preventDefault()}>
                                <div>
                                    <div className="kachel-label uppercase text-gray-400">Kaltmiete / Monat</div>
                                    {/* Eine Stufe kleiner auf schmalen Schirmen: bei
                                        1024 px ragte der Betrag 10 px aus der Karte. */}
                                    <div className="text-base xl:text-xl font-bold text-slate-900 tabular-nums">{euro(e.monat_kalt)}</div>
                                    {e.unbekannt > 0 && (
                                        <Link to={`/einheiten?entity_id=${e.entity_id}&status=unbekannt`}
                                              draggable={false}
                                              onMouseDown={(ev) => ev.stopPropagation()}
                                              onDragStart={(ev) => ev.preventDefault()}
                                              onClick={(ev) => ev.stopPropagation()}
                                              className="block text-[10px] text-orange-600 mt-0.5 hover:underline underline-offset-2">
                                            {beurteilbar} von {e.units} Einheiten erfasst
                                        </Link>
                                    )}
                                </div>
                                <Link to={`/entitaeten/${e.entity_id}`} onClick={(ev) => ev.stopPropagation()}>
                                    <Button variant="outline" size="sm" className="h-8 text-xs">Cockpit</Button>
                                </Link>
                            </div>
                        </div>
                    );
                })}
                {(!cockpit || entities.length === 0) && (
                    <div className="col-span-full py-16 text-center text-sm text-gray-400 border border-dashed border-gray-200 rounded-md">
                        Keine Entitäten vorhanden.
                    </div>
                )}
            </div>
        </div>
    );
};

// View "Asset" — Verteilung nach Typ + Status
const ViewAsset = ({ cockpit }) => {
    if (!cockpit) return null;
    const typs = [
        { key: "wohnung", label: "Wohnungen", color: "bg-slate-900" },
        { key: "gewerbe", label: "Gewerbe", color: "bg-amber-500" },
        { key: "garage", label: "Garagen", color: "bg-blue-500" },
        { key: "stellplatz", label: "Stellplätze", color: "bg-emerald-500" },
    ];
    const totalTyp = Object.values(cockpit.by_typ || {}).reduce((a, b) => a + b, 0) || 1;
    const statuses = [
        { key: "vermietet", label: "Vermietet", tone: "bg-emerald-500" },
        { key: "leer", label: "Leer (bestätigt)", tone: "bg-gray-400" },
        { key: "unbekannt", label: "Unbekannt", tone: "bg-orange-500" },
        { key: "in_uebergabe", label: "In Übergabe", tone: "bg-amber-500" },
    ];
    const totalStatus = Object.values(cockpit.by_status || {}).reduce((a, b) => a + b, 0) || 1;
    // Die Leerstandsquote gehört auf den beurteilbaren Teil gerechnet, nicht auf
    // den ganzen Bestand: Einheiten ohne Mieterdaten verwässern sie sonst nach
    // unten und lassen den Leerstand kleiner aussehen, als er sein kann.
    const dq = cockpit.datenqualitaet || {};
    const beurteilbar = dq.units_beurteilbar || 0;
    const leerQuote = beurteilbar ? Math.round(((cockpit.by_status?.leer || 0) / beurteilbar) * 100) : null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="view-asset">
            <div className="bg-white border border-gray-200 rounded-md p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Nach Asset-Typ</div>
                <div className="mt-4 space-y-3">
                    {typs.map((t) => {
                        const n = cockpit.by_typ?.[t.key] || 0;
                        const pct = Math.round((n / totalTyp) * 100);
                        return (
                            <div key={t.key}>
                                <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="font-medium text-slate-900">{t.label}</span>
                                    <span className="font-mono tabular-nums text-slate-600">{n} · {pct}%</span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full ${t.color} transition-all duration-500`} style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Nach Status</div>
                <div className="mt-4 space-y-3">
                    {statuses.map((s) => {
                        const n = cockpit.by_status?.[s.key] || 0;
                        const pct = Math.round((n / totalStatus) * 100);
                        return (
                            <div key={s.key}>
                                <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="font-medium text-slate-900">{s.label}</span>
                                    <span className="font-mono tabular-nums text-slate-600">{n} · {pct}%</span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div className={`h-full ${s.tone} transition-all duration-500`} style={{ width: `${pct}%` }} />
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="mt-6 pt-6 border-t border-gray-100 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                        <div className="kachel-label uppercase text-gray-400">Leerstand</div>
                        <div className="text-2xl font-bold text-slate-900 tabular-nums" data-testid="asset-leerstand">
                            {leerQuote === null ? "—" : `${leerQuote}%`}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                            {beurteilbar ? `von ${beurteilbar} beurteilbaren` : "keine Grundlage"}
                        </div>
                    </div>
                    <div>
                        <div className="kachel-label uppercase text-gray-400">Vermietet</div>
                        <div className="text-2xl font-bold text-slate-900 tabular-nums">{cockpit.by_status.vermietet || 0}</div>
                    </div>
                    <div>
                        <div className="kachel-label uppercase text-gray-400">Unbekannt</div>
                        <div className={`text-2xl font-bold tabular-nums ${dq.units_unbekannt ? "text-orange-600" : "text-slate-900"}`}
                             data-testid="asset-unbekannt">
                            {dq.units_unbekannt || 0}
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">Mieterdaten fehlen</div>
                    </div>
                    <div>
                        <div className="kachel-label uppercase text-gray-400">In Übergabe</div>
                        <div className="text-2xl font-bold text-slate-900 tabular-nums">{cockpit.by_status.in_uebergabe || 0}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// View "Prozess" — Kanban der Mieterwechsel
const ViewProzess = ({ turnovers, units, tenants, properties }) => {
    const columns = PHASES.map((p) => ({
        phase: p,
        items: turnovers.filter((t) => t.current_phase === p.id && t.status !== "abgeschlossen"),
    }));

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="view-prozess">
            {columns.map(({ phase, items }) => (
                <div key={phase.id} className="bg-[#FAFAFA] border border-gray-200 rounded-md p-3 min-h-[280px] flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400">Phase {phase.id}</div>
                            <div className="font-heading font-semibold text-slate-900 text-sm">{phase.short}</div>
                        </div>
                        <span className="text-xs font-mono bg-white border border-gray-200 rounded-full px-2 py-0.5 text-slate-700 tabular-nums">{items.length}</span>
                    </div>
                    <div className="flex-1 space-y-2">
                        {items.length === 0 && (
                            <div className="text-xs text-gray-400 py-8 text-center border border-dashed border-gray-200 rounded-md">Keine Vorgänge</div>
                        )}
                        {items.map((t) => {
                            const u = units.find((x) => x.id === t.unit_id);
                            const p = u ? properties.find((x) => x.id === u.property_id) : null;
                            const oldT = tenants.find((x) => x.id === t.old_tenant_id);
                            return (
                                <Link key={t.id} to={`/wechsel/${t.id}`} className="block bg-white border border-gray-200 rounded-md p-3 hover:border-slate-400 hover:shadow-flat transition-all">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-medium text-sm text-slate-900 truncate">{u?.unit_label || "Einheit"}</div>
                                            <div className="text-xs text-gray-500 truncate">{p?.label || p?.address || "—"}</div>
                                        </div>
                                        <StatusBadge status={t.status} />
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between text-xs">
                                        <span className="text-gray-500 truncate">{oldT?.full_name || "—"}</span>
                                        <span className="text-gray-400 tabular-nums">{germanDate(t.updated_at)}</span>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

// Cockpit-Reiter "Aufgaben": was insgesamt offen ist, mit den dringendsten
// zuerst. Die ganze Liste mit allen Spalten steht unter /aufgaben.
const ViewAufgaben = ({ daten }) => {
    if (!daten) return <div className="text-sm text-gray-400">Lädt …</div>;
    const offen = (daten.aufgaben || []).filter((a) => !a.is_completed);
    const jeBearbeiter = {};
    offen.forEach((a) => {
        const k = a.assigned_to || "— ohne Bearbeiter";
        jeBearbeiter[k] = (jeBearbeiter[k] || 0) + 1;
    });
    const jePrio = { hoch: 0, normal: 0, niedrig: 0 };
    offen.forEach((a) => { jePrio[a.priority] = (jePrio[a.priority] || 0) + 1; });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="view-aufgaben">
            <div className="bg-white border border-gray-200 rounded-md p-6 space-y-4">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500">Stand</div>
                <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                        <div className="kachel-label uppercase text-gray-400">Offen</div>
                        <div className="text-3xl font-bold text-slate-900 tabular-nums">{daten.offen}</div>
                    </div>
                    <div>
                        <div className="kachel-label uppercase text-gray-400">Überfällig</div>
                        <div className={`text-3xl font-bold tabular-nums ${daten.ueberfaellig ? "text-red-600" : "text-slate-900"}`}>
                            {daten.ueberfaellig}
                        </div>
                    </div>
                </div>
                <div className="pt-4 border-t border-gray-100 space-y-1.5">
                    {["hoch", "normal", "niedrig"].map((prio) => (
                        <div key={prio} className="flex items-baseline justify-between text-xs">
                            <span className="text-gray-500 capitalize">{prio}</span>
                            <span className="text-slate-700 font-medium tabular-nums">{jePrio[prio] || 0}</span>
                        </div>
                    ))}
                </div>
                <Link to="/aufgaben">
                    <Button variant="outline" size="sm" className="w-full h-8" data-testid="link-aufgaben">
                        Alle Aufgaben
                    </Button>
                </Link>
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-4">Nach Bearbeiter</div>
                {Object.keys(jeBearbeiter).length === 0 && (
                    <div className="text-sm text-gray-400">Keine offene Aufgabe.</div>
                )}
                <div className="space-y-2">
                    {Object.entries(jeBearbeiter).sort((a, b) => b[1] - a[1]).map(([name, n]) => (
                        <div key={name} className="flex items-baseline justify-between text-sm">
                            <span className={name.startsWith("—") ? "text-gray-400" : "text-slate-900"}>{name}</span>
                            <span className="text-slate-700 font-medium tabular-nums">{n}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-md p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-gray-500 mb-4">Als Nächstes</div>
                {offen.length === 0 && <div className="text-sm text-gray-400">Nichts offen.</div>}
                <div className="space-y-2">
                    {offen.slice(0, 6).map((a) => (
                        <Link key={a.id} to="/aufgaben"
                              className="block border border-gray-100 rounded px-3 py-2 hover:border-slate-400">
                            <div className="text-sm text-slate-900 truncate flex items-center gap-1.5">
                                {a.ueberfaellig && <AlertTriangle size={11} className="text-red-600 shrink-0" />}
                                {a.title}
                            </div>
                            <div className="text-[11px] text-gray-500 truncate">
                                {[a.property_address, a.unit_label].filter(Boolean).join(" · ") || "—"}
                                {a.due_date ? ` · fällig ${germanDate(a.due_date)}` : ""}
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
};

// Die Arbeitsliste: was ansteht, nach Dringlichkeit, mit Sprungziel. Sie
// rechnet nichts selbst -- sie liest die Ampeln der Module. Jede Zeile kommt
// aus Daten, die ohnehin gepflegt werden; keine verlangt eine neue Eingabe.
// Ist sie leer, heisst das "nichts zu tun" und nicht "niemand hat eingetragen".
const Arbeitsliste = ({ daten }) => {
    if (!daten) return null;
    // Gruen nur, wenn ALLE Quellen geantwortet haben. Sonst hiesse "nichts
    // offen", dass niemand nachgesehen hat -- der Fehler, den die Liste
    // gerade verhindern soll.
    if (!daten.anzahl && daten.vollstaendig) {
        return (
            <div className="bg-emerald-50 border border-emerald-200 rounded-md p-5 flex items-center gap-3"
                 data-testid="arbeitsliste-leer">
                <CheckCircle2 size={18} className="text-emerald-600 shrink-0" />
                <div className="text-sm text-emerald-900">
                    Nichts offen — keine Vorschläge, keine Widersprüche, keine überfälligen Aufgaben.
                </div>
            </div>
        );
    }
    const ton = (d) => (d === "ausfall"
        ? { rand: "border-l-gray-400", zahl: "text-gray-400" }
        : d === "hoch"
        ? { rand: "border-l-red-500", zahl: "text-red-700" }
        : { rand: "border-l-amber-400", zahl: "text-amber-700" });
    return (
        <div className="bg-white border border-gray-200 rounded-md overflow-hidden" data-testid="arbeitsliste">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
                <ListChecks size={15} className="text-slate-700" />
                <span className="font-heading font-semibold text-slate-900">Das steht an<Hilfe id="arbeitsliste" /></span>
                <span className="text-xs text-gray-500">— {daten.anzahl} Stapel, oben beginnen</span>
                {daten.ausfaelle > 0 && (
                    <span className="text-xs text-gray-500 inline-flex items-center gap-1"
                          title="Eine Prüfung hat nicht geantwortet — die Liste ist unvollständig">
                        <AlertTriangle size={11} className="text-gray-400" />
                        {daten.ausfaelle} Prüfung{daten.ausfaelle > 1 ? "en" : ""} fehlt
                    </span>
                )}
            </div>
            <div className="divide-y divide-gray-100">
                {daten.zeilen.map((z, i) => {
                    const t = ton(z.dringlichkeit);
                    return (
                        <Link key={i} to={z.ziel}
                              className={`flex items-center gap-4 px-5 py-3 border-l-4 ${t.rand} hover:bg-gray-50`}
                              data-testid={`arbeit-${i}`}>
                            <div className="w-24 shrink-0 text-right">
                                <span className={`text-2xl font-bold tabular-nums ${t.zahl}`}>{z.zahl}</span>
                                {z.einheit && <div className="text-[10px] text-gray-400 uppercase tracking-wider">{z.einheit}</div>}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-slate-900 font-medium">{z.titel}</div>
                                <div className="text-xs text-gray-500 truncate">{z.warum}</div>
                            </div>
                            {z.betrag ? (
                                <div className="text-sm tabular-nums text-gray-600 shrink-0">{euro(z.betrag)}</div>
                            ) : null}
                            <ChevronRight size={16} className="text-gray-300 shrink-0" />
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

const Dashboard = () => {
    const [cockpit, setCockpit] = useState(null);
    const [turnovers, setTurnovers] = useState([]);
    const [units, setUnits] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [properties, setProperties] = useState([]);
    const [tab, setTab] = useState("besitz");
    // Die steuernde Zahl laeuft getrennt vom uebrigen Laden: faellt sie aus,
    // bleibt das Cockpit stehen und die Kachel zeigt einen Strich.
    const [erkl, setErkl] = useState(null);
    const [aufgaben, setAufgaben] = useState(null);
    const [wider, setWider] = useState(null);
    const [arbeit, setArbeit] = useState(null);

    const load = async () => {
        const [c, tv, u, te, p] = await Promise.all([
            getCockpitStats(), listTurnovers(), listUnits(), listTenants(), listProperties(),
        ]);
        setCockpit(c); setTurnovers(tv); setUnits(u); setTenants(te); setProperties(p);
        erklaert(2026).then(setErkl).catch(() => setErkl(null));
        listAufgaben().then(setAufgaben).catch(() => setAufgaben(null));
        listWidersprueche().then(setWider).catch(() => setWider(null));
        arbeitsliste().then(setArbeit).catch(() => setArbeit(null));
    };
    useEffect(() => { load(); }, []);

    const handleSeed = () => {
        seedDemo().then(() => {
            toast.success("Demo-Daten geladen");
            load();
        });
    };

    // Einheiten und Miete nach Typ — nur Typen, die im Bestand vorkommen.
    const unitBreakdown = useMemo(
        () => Object.entries(cockpit?.by_typ || {})
            .filter(([, n]) => n > 0)
            .sort((a, b) => b[1] - a[1])
            .map(([k, n]) => ({ label: typLabel(k), value: n, href: `/einheiten?typ=${k}` })),
        [cockpit],
    );
    const rentBreakdown = useMemo(
        () => Object.entries(cockpit?.rent_by_typ || {})
            .filter(([, v]) => (v.summe || 0) > 0)
            .sort((a, b) => (b[1].summe || 0) - (a[1].summe || 0))
            .map(([k, v]) => ({ label: typLabel(k), value: euro(v.summe), href: `/einheiten?typ=${k}` })),
        [cockpit],
    );
    // Die Kachel zeigt die gesamte Soll-Miete, also Kalt- plus Pauschalmieten.
    // Wohnungen und Gewerbe laufen über kaltmiete, Garagen und Stellplätze
    // über pauschalmiete — die Fußnote macht die Aufteilung sichtbar.
    const rentPauschal = (cockpit?.rent_total ?? 0) - (cockpit?.rent_kalt_total ?? 0);
    // Die Soll-Miete ist nur so vollstaendig wie die Mieterdaten. Ohne diesen
    // Zusatz liest man sie als Gesamtmiete des Portfolios, obwohl sie die Miete
    // des bekannten Teils ist -- bei 35 Einheiten ohne Mieterdatensatz ein
    // Unterschied, der Entscheidungen veraendert.
    const dq = cockpit?.datenqualitaet;
    const rentFussnote = cockpit
        ? `davon ${euro(cockpit.rent_kalt_total)} kalt · ${euro(rentPauschal)} pauschal`
          + (dq?.rent_basis_units ? ` · auf ${dq.rent_basis_units} von ${dq.units_gesamt} Einheiten` : "")
        : null;

    return (
        <div className="px-6 md:px-10 py-8 space-y-8">
            {/* Hero */}
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Gesamt-Cockpit</div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight" data-testid="dashboard-title">
                        Portfolio auf einen Blick
                    </h1>
                    <p className="text-sm text-gray-500 mt-1 max-w-xl">
                        Entitäten, Immobilien und Mieteinheiten konsolidiert. Drei Perspektiven: <b>Besitz</b>, <b>Asset</b>, <b>Prozess</b>.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <BotStatusBadge />
                    <Button variant="outline" size="sm" onClick={handleSeed} data-testid="btn-seed">
                        <Sparkles size={14} className="mr-1.5" /> Demo-Daten
                    </Button>
                    <Link to="/wechsel/neu">
                        <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="btn-new-turnover">
                            <Plus size={14} className="mr-1.5" /> Neuer Mieterwechsel
                        </Button>
                    </Link>
                </div>
            </div>

            <Arbeitsliste daten={arbeit} />

            {/* Metrics */}
            {/* Neun Kacheln nebeneinander erst ab sehr breiten Schirmen: bei
                1280 px blieben je Kachel 78 px Innenbreite, und da passt kein
                Geldbetrag hinein. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-9 gap-4">
                {/* Die steuernde Zahl des Fahrplans (Paragraph 3): erklaert ist
                    eine Soll-Position, wenn bestaetigte Zuordnungen sie decken
                    oder eine Klaerung den Rest anerkennt. Vorschlaege zaehlen
                    nicht -- deshalb steht sie zuerst: sie ist die Arbeitsliste. */}
                <Metric
                    label="Miete erklärt 2026"
                    value={erkl?.prozent == null ? "—" : `${String(erkl.prozent).replace(".", ",")} %`}
                    icon={CheckCircle2}
                    testid="metric-erklaert"
                    href="/mietkonto/durchsicht"
                    footnote={erkl
                        ? `${erkl.zaehler} von ${erkl.nenner} Soll-Positionen`
                          // Gestundetes zaehlt nicht als erklaert -- das Geld ist
                          // noch nicht da --, soll aber sichtbar warten.
                          + ` · ${erkl.gestundet ?? 0} gestundet`
                          + (erkl.letzte_buchung ? ` · Buchungen bis ${germanDate(erkl.letzte_buchung)}` : "")
                        : null}
                />
                {/* Widersprueche: was nicht zusammenpasst, kostet Geld, nicht Schoenheit.
                    Deshalb neben der steuernden Zahl und nicht in einer Unterseite. */}
                <Metric
                    label="Widersprüche"
                    value={wider?.gesamt ?? "—"}
                    icon={ShieldAlert}
                    testid="metric-widersprueche"
                    href="/widersprueche"
                    footnote={wider
                        ? `${wider.gruppen_offen} von ${wider.gruppen_geprueft} Prüfungen`
                          + (wider.summe_betroffen ? ` · ${euro(wider.summe_betroffen)} betroffen` : "")
                        : null}
                />
                <Metric label="Entitäten" value={cockpit?.total_entities ?? 0} icon={Building} testid="metric-entities" href="/entitaeten" />
                <Metric label="Immobilien" value={cockpit?.total_properties ?? 0} icon={Building2} testid="metric-properties" href="/immobilien" />
                <Metric label="Einheiten" value={cockpit?.total_units ?? 0} icon={Home} testid="metric-units" breakdown={unitBreakdown} href="/einheiten" />
                <Metric label="Mieter" value={cockpit?.total_tenants ?? 0} icon={Users} testid="metric-tenants" href="/mieter" />
                <Metric label="Offene Wechsel" value={cockpit?.open_turnovers ?? 0} icon={ArrowLeftRight} testid="metric-open" href="/wechsel" />
                <Metric
                    label="Soll-Miete/Mo."
                    value={euro(cockpit?.rent_total ?? 0)}
                    icon={TrendingUp}
                    testid="metric-miete"
                    breakdown={rentBreakdown}
                    href="/mietkonto"
                    footnote={rentFussnote}
                />
                <Metric
                    label="Datenqualität"
                    value={dq ? `${dq.quote}%` : "—"}
                    icon={ShieldCheck}
                    testid="metric-datenqualitaet"
                    footnote={dq ? `${dq.units_beurteilbar} von ${dq.units_gesamt} Einheiten beurteilbar` : null}
                />
            </div>

            {/* Datenluecke — steht bewusst direkt unter den Kennzahlen, weil sie
                deren Grundlage betrifft und nicht eine Randnotiz ist. */}
            {dq?.units_unbekannt > 0 && (
                <div className="bg-orange-50 border border-orange-200 rounded-md p-4 flex items-start gap-3" data-testid="alert-datenluecke">
                    <AlertCircle size={16} className="text-orange-600 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="text-sm font-medium text-orange-900">
                            Bei {dq.units_unbekannt} von {dq.units_gesamt} Einheiten fehlen die Mieterdaten
                        </div>
                        <div className="text-xs text-orange-800 mt-1">
                            Die Kennzahlen oben beruhen auf {dq.units_beurteilbar} beurteilbaren Einheiten
                            {dq.rent_basis_units ? `, die Soll-Miete auf ${dq.rent_basis_units}` : ""}.
                            „Unbekannt“ heißt nicht leer — es heißt, dass hier niemand erfasst ist.
                            {dq.vermietet_ohne_mieter > 0 && ` Zusätzlich behaupten ${dq.vermietet_ohne_mieter} Einheiten „vermietet“, ohne dass ein Mieter daran hängt.`}
                            {dq.leer_mit_mieter > 0 && ` ${dq.leer_mit_mieter} Einheiten stehen auf „leer“, haben aber einen aktiven Mieter.`}
                        </div>
                    </div>
                    <div className="flex flex-col gap-2">
                        <Link to="/einheiten?status=unbekannt">
                            <Button variant="outline" size="sm" className="h-8 whitespace-nowrap w-full">Einheiten zeigen</Button>
                        </Link>
                        <Link to="/mieter/fehlend" data-testid="link-fehlende-mieter">
                            <Button variant="outline" size="sm" className="h-8 whitespace-nowrap w-full">Fehlende Mietverhältnisse</Button>
                        </Link>
                    </div>
                </div>
            )}

            {/* Views */}
            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-gray-100 p-1 rounded-md">
                    <TabsTrigger value="besitz" data-testid="tab-besitz">
                        <Building size={14} className="mr-1.5" /> Besitz
                    </TabsTrigger>
                    <TabsTrigger value="asset" data-testid="tab-asset">
                        <CircleDot size={14} className="mr-1.5" /> Asset
                    </TabsTrigger>
                    <TabsTrigger value="prozess" data-testid="tab-prozess">
                        <ArrowLeftRight size={14} className="mr-1.5" /> Prozess
                    </TabsTrigger>
                    <TabsTrigger value="aufgaben" data-testid="tab-aufgaben">
                        <CheckSquare size={14} className="mr-1.5" /> Aufgaben
                        {aufgaben?.ueberfaellig > 0 && (
                            <span className="ml-1.5 text-[10px] bg-red-100 text-red-700 rounded-full px-1.5">
                                {aufgaben.ueberfaellig}
                            </span>
                        )}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="besitz" className="mt-6">
                    <div className="mb-3 text-sm text-gray-500">Portfolio-Performance nach Eigentümer-Entität</div>
                    <ViewBesitz cockpit={cockpit} />
                </TabsContent>

                <TabsContent value="asset" className="mt-6">
                    <div className="mb-3 text-sm text-gray-500">Verteilung nach Nutzungsart und Status — schnelle Leerstand-Analyse</div>
                    <ViewAsset cockpit={cockpit} />
                </TabsContent>

                <TabsContent value="prozess" className="mt-6">
                    <div className="mb-3 text-sm text-gray-500">Laufende Mieterwechsel nach Phase</div>
                    <ViewProzess turnovers={turnovers} units={units} tenants={tenants} properties={properties} />
                </TabsContent>

                <TabsContent value="aufgaben" className="mt-6">
                    <div className="mb-3 text-sm text-gray-500">Was im Bestand offen ist — nach Priorität, Bearbeiter und Fälligkeit</div>
                    <ViewAufgaben daten={aufgaben} />
                </TabsContent>
            </Tabs>

            {/* Alerts / In-Übergabe list */}
            {cockpit?.by_status?.in_uebergabe > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 flex items-center gap-3">
                    <AlertCircle size={16} className="text-amber-600 shrink-0" />
                    <div className="flex-1">
                        <div className="text-sm font-medium text-amber-900">
                            {cockpit.by_status.in_uebergabe} {cockpit.by_status.in_uebergabe === 1 ? "Einheit" : "Einheiten"} aktuell in Übergabe
                        </div>
                        <div className="text-xs text-amber-700">Öffnen Sie den Prozess-Tab für Details.</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setTab("prozess")} className="h-8">Prozesse öffnen</Button>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
